import { labelPairs } from '@glow/ai-contract'
import { tUnchecked } from '@glow/language'
import { createContext, ReactNode, useContext, useMemo } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { getCurrentLanguageCode } from '../../i18n/languages'
import { LABEL_NAMESPACES } from '../../i18n/otherAppLabels'
import { AiThinkingParcel } from './AiThinkingParcel'

const remarkPlugins = [remarkGfm]

const components = {
  // Never an <img>: the browser GETs an image URL without a click, so a model tricked by
  // retrieved content into emitting one would exfiltrate data through the request itself
  img: () => null,
  p: ({ children }: { children?: ReactNode }) => <p className="text-sm text-emphasis-high">{children}</p>,
  h1: ({ children }: { children?: ReactNode }) => (
    <h1 className="text-base font-semibold text-emphasis-high">{children}</h1>
  ),
  h2: ({ children }: { children?: ReactNode }) => (
    <h2 className="text-sm font-semibold text-emphasis-high">{children}</h2>
  ),
  h3: ({ children }: { children?: ReactNode }) => (
    <h3 className="text-sm font-semibold text-emphasis-high">{children}</h3>
  ),
  ul: ({ children }: { children?: ReactNode }) => (
    <ul className="list-disc pl-5 text-sm text-emphasis-high">{children}</ul>
  ),
  ol: ({ children }: { children?: ReactNode }) => (
    <ol className="list-decimal pl-5 text-sm text-emphasis-high">{children}</ol>
  ),
  li: ({ children }: { children?: ReactNode }) => <li className="mb-1">{children}</li>,
  strong: ({ children }: { children?: ReactNode }) => <strong className="font-semibold">{children}</strong>,
  code: ({ children }: { children?: ReactNode }) => (
    <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">{children}</code>
  ),
  pre: ({ children }: { children?: ReactNode }) => (
    <pre className="overflow-x-auto rounded bg-gray-100 p-2 text-xs">{children}</pre>
  ),
  a: ({ children, href }: { children?: ReactNode; href?: string }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline">
      {children}
    </a>
  ),
  table: ({ children }: { children?: ReactNode }) => (
    <div className="overflow-x-auto">
      <table className="border-collapse text-sm text-emphasis-high">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: ReactNode }) => <thead className="bg-gray-100">{children}</thead>,
  th: ({ children }: { children?: ReactNode }) => (
    <th className="border border-gray-300 px-2 py-1 text-left text-sm font-semibold text-emphasis-high">{children}</th>
  ),
  td: ({ children }: { children?: ReactNode }) => (
    <td className="border border-gray-300 px-2 py-1 text-sm text-emphasis-high">{children}</td>
  )
}

// Labels arrive from the documents as **Text** (`a.b`); the key is looked up as the app does
const LABEL_WITH_KEY = /\*\*((?:\\\*|[^*\n])+)\*\* ?\(`([a-zA-Z][\w-]*(?:\.[\w-]+)*)`\)/g
// An annotation cut off at the end of the text, by the stream or by Stop
const PARTIAL_KEY = /\*\* ?\((?:`[\w.-]*`?)?$/
// With a key set any cut-off annotation goes, whatever it holds, since only a given key may follow a label
const PARTIAL_ANNOTATION = /\*\* ?\((?:`[^`\n]*`?)?$/

// Placeholders stay as the documents quote them
const catalogueText = (key: string, ns: string | string[], languages?: string[]): string | null => {
  const value: unknown = tUnchecked(key, {
    ns,
    lngs: languages,
    returnObjects: true,
    defaultValue: '',
    skipInterpolation: true
  })
  return typeof value === 'string' && value !== '' ? value : null
}

// Case, surrounding space and closing punctuation are what a model most often changes in a quote
const comparable = (text: string): string =>
  text
    .replace(/^\\([*_])/, '$1')
    .trim()
    .replace(/[.:!?\u2026]+$/, '')
    .toLowerCase()

/*
The docs quote a label's text next to its key, so the app whose catalogue holds that text under
that key, in English or in the user's language, is the app the label belongs to; that settles a
key two apps share with different words. Failing that, the user's language is tried across every
app before any English fallback: i18next would otherwise exhaust one app's fallback languages
before looking at the next, and Glow's English would hide the portal's Norwegian. A translation
from any app beats English from the owner. i18next also consults the shared `common` namespace
after each lookup, which holds no key these apps hold.
*/
const translation = (key: string, quoted: string): string | null => {
  const language = getCurrentLanguageCode()
  const wanted = comparable(quoted)
  const owner = LABEL_NAMESPACES.find((ns) =>
    [catalogueText(key, ns, ['en']), catalogueText(key, ns, [language])].some(
      (text) => text !== null && comparable(text) === wanted
    )
  )
  return (
    (owner === undefined ? null : catalogueText(key, owner, [language])) ??
    catalogueText(key, LABEL_NAMESPACES, [language]) ??
    catalogueText(key, LABEL_NAMESPACES)
  )
}

// A catalogue text is a template: a bracketed {{value}} is dropped, any other reads as …
const withoutPlaceholders = (label: string): string =>
  label.replace(/\s*\(\{\{[^{}]*\}\}\)/g, '').replace(/\{\{[^{}]*\}\}/g, '…')

// A leading * or _ would open markdown, not a label
const bold = (label: string): string => `**${withoutPlaceholders(label).replace(/^([*_])/, '\\$1')}**`
// A code constant in a label's shape, such as an order state; no catalogue key is written this way
const CONSTANT = /^[A-Z][A-Z0-9_]*$/

// An id the user asked about, as a shipment number; it stays as written like a constant
const ID = /^\d+$/

// `allowed` holds the keys the answer's excerpts carried: any other key was made up, so only its label stays (a
// constant or id stays as written); answers stored without the set are judged by the key's shape, as before
export const localizeLabels = (text: string, allowed?: string): string =>
  allowed === undefined ? localizeByShape(text) : localizeAllowed(text, new Set(allowed.split('\n')))

const localizeAllowed = (text: string, allowed: ReadonlySet<string>): string =>
  text
    .replace(labelPairs(), (match, label: string, key: string) =>
      allowed.has(key)
        ? bold(translation(key, label) ?? label)
        : CONSTANT.test(key) || ID.test(key)
          ? match
          : bold(label)
    )
    .replace(PARTIAL_ANNOTATION, '**')

const localizeByShape = (text: string): string =>
  text
    .replace(LABEL_WITH_KEY, (match, label: string, key: string) => {
      const translated = translation(key, label)
      // A key no catalogue holds (one the model garbled) shows its text and hides the key; only
      // a constant is left as the model wrote it
      return translated === null && CONSTANT.test(key) ? match : bold(translated ?? label)
    })
    .replace(PARTIAL_KEY, '**')

// Counts the label catalogues loaded so far, which localizeLabels reads from i18next
export const OtherAppLabelsLoadedContext = createContext(0)

export const useLocalizeLabels = (): ((text: string, allowed?: string) => string) => {
  const labelsLoaded = useContext(OtherAppLabelsLoadedContext)
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new function per load is what makes memoized callers localize again
  return useMemo(() => (text: string, allowed?: string) => localizeLabels(text, allowed), [labelsLoaded])
}

export const AiAnswer = ({
  text,
  streaming,
  thinking,
  labelKeys
}: {
  text: string
  streaming: boolean
  thinking?: ReactNode
  labelKeys?: string
}) => {
  const localize = useLocalizeLabels()
  return streaming && !text ? (
    (thinking ?? <AiThinkingParcel />)
  ) : (
    <div className="flex flex-col gap-2">
      <Markdown remarkPlugins={remarkPlugins} components={components}>
        {localize(text, labelKeys)}
      </Markdown>
      {streaming && <span className="animate-pulse text-primary-500">▍</span>}
    </div>
  )
}
